import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { UserSelect } from '../ui/UserSelect';

interface User {
  id: string;
  email: string;
  username: string;
  phone?: string;
  password: string;
  role: 'admin' | 'user' | 'responsable' | 'pasteur';
}

export default function LoginPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login, user: _user } = useAuth();

  // Mapping des IDs de rapports vers les routes correspondantes
  const reportRoutesMap = {
    'worshipReports': '/worship-reports',
    'adnReports': '/adn-reports',
    'financeReports': '/finance-reports',
    'academieReports': '/academie-reports',
    'sainteCeneReports': '/sainte-cene-reports',
    'sonoReports': '/sono-reports'
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Ne pas inclure le mot de passe dans les données chargées
        const { password: _, ...userWithoutPassword } = data;
        return {
          id: doc.id,
          ...userWithoutPassword
        };
      }) as User[];
      // Dédoublonner par email et trier alphabétiquement par nom
      const uniqueUsers = usersData.filter((user, index, self) =>
        index === self.findIndex(u => u.email === user.email)
      );
      const sortedUsers = uniqueUsers.sort((a, b) =>
        a.username.localeCompare(b.username, 'fr', { sensitivity: 'base' })
      );
      setUsers(sortedUsers);
    };

    fetchUsers();
  }, []);

  // Réinitialiser le mot de passe quand l'utilisateur change
  useEffect(() => {
    setPassword('');
    setError('');
  }, [selectedEmail]);

  // Fonction pour déterminer la route de redirection
  const getRedirectRoute = (userData: any) => {
    // Si c'est un responsable avec des rapports assignés
    if (userData.role === 'responsable' && userData.assignedReports && userData.assignedReports.length > 0) {
      const firstAssignedReport = userData.assignedReports[0];
      const route = reportRoutesMap[firstAssignedReport as keyof typeof reportRoutesMap];
      return route || '/dashboard';
    }
    
    // Pour tous les autres cas (admin, pasteur, user, ou responsable sans assignation)
    return '/dashboard';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedEmail || !password) {
      setError('Veuillez sélectionner un email et entrer un mot de passe');
      return;
    }

    try {
      // L'authentification (vérification mot de passe + log) est gérée par AuthContext.login
      const userToStore = await login(selectedEmail, password);
      if (!userToStore) {
        setError('Utilisateur non trouvé');
        return;
      }

      const redirectRoute = getRedirectRoute(userToStore);
      navigate(redirectRoute);
    } catch (error: any) {
      console.error('Login error:', error);
      const code = error?.message;
      if (code === 'ACCOUNT_DISABLED') {
        setError('Votre compte a été désactivé. Contactez un administrateur.');
      } else if (code === 'INVALID_PASSWORD') {
        setError('Mot de passe incorrect');
      } else if (code === 'USER_NOT_FOUND') {
        setError('Utilisateur non trouvé');
      } else {
        setError('Email ou mot de passe incorrect');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-between bg-gradient-to-br from-[#00665C]/10 to-[#F2B636]/10">
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="text-center">
            <img
              src="/images/LOGO_VH.png"
              alt="Logo"
              className="mx-auto h-20 w-auto mb-6"
            />
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
              Plateforme de gestion
            </h2>
            <p className="mt-2 text-xl font-semibold text-[#00665C]">
              Vases d'Honneur Angré Djibi 3
            </p>
            <p className="mt-4 text-base text-gray-600">
              Connectez-vous à votre compte
            </p>
          </div>

          <div className="mt-8 bg-white/80 backdrop-blur-sm py-8 px-6 shadow-xl rounded-2xl border border-white/50">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <label htmlFor="user-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Sélectionner un utilisateur
                  </label>
                  <UserSelect
                    value={selectedEmail}
                    onChange={setSelectedEmail}
                    users={users}
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Entrez votre mot de passe"
                      required
                      disabled={!selectedEmail}
                      className="appearance-none block w-full pl-10 pr-12 py-3.5 border border-gray-200 rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C] transition-all duration-200 sm:text-sm bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      disabled={!selectedEmail}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 py-3 px-4 rounded-xl border border-red-100 shadow-sm">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={!selectedEmail || !password}
                  className="group relative w-full flex justify-center py-3.5 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-[#00665C] hover:bg-[#00665C]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00665C] transition-all duration-200 shadow-sm hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-sm"
                >
                  Se connecter
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <footer className="py-4 text-center text-sm text-gray-600 bg-white bg-opacity-50">
        <p>© {new Date().getFullYear()} Vases d'Honneur Angré Djibi 3. Tous droits réservés.</p>
        <p className="mt-1 text-xs">Développé avec ❤️ par l'équipe technique Vases d'Honneur Angré Djibi 3</p>
      </footer>
    </div>
  );
}
